import {SupportTriggerPointTriggerPoint} from '../../../generated/types';

const AssistantTriggerPoints: SupportTriggerPointTriggerPoint[] = [
  'assistant_search_result_direct',
  'assistant_search_result_suggested_query',
  'assistant_onboarding',
  'payments_analytics',
  'dashboard_help_icon',
  'terminal_shop_assistant_cta',
];

const StartFreshTriggerPoints: SupportTriggerPointTriggerPoint[] = [
  'assistant_search_result_direct',
  'assistant_search_result_suggested_query',
  'assistant_onboarding',
  'payments_analytics',
  'terminal_shop_assistant_cta',
];

// How opening the assistant from a trigger point should treat the current
// conversation in the threads experience. Each behavior only applies when
// the assistant transitions from closed to open; when it is already open
// (or minimized with a conversation loaded), every kind of trigger point
// continues the current conversation, except that 'inject_or_new' also
// sends its query into it.
// - 'resume_latest': blanket entry points (e.g. the Dashboard help icon).
//   On open, reload the current conversation if it is still active, else
//   the latest active conversation, else start a new one. Ended
//   conversations are never reopened. Unknown trigger points default to
//   this.
// - 'start_new': contextual product entry points (e.g. Checkout Studio).
//   On open, start a new conversation carrying the trigger context, even
//   when an active conversation exists.
// - 'inject_or_new': smart-suggestion entry points that carry a query
//   (e.g. PayIntel). When the assistant is already open or minimized, send
//   the query into the current thread as a user message (falling back to a
//   new thread if the current one is live support). On open from closed,
//   start a new conversation with the query.
export type TriggerPointOpenBehavior =
  | 'resume_latest'
  | 'start_new'
  | 'inject_or_new';

const CheckoutStudioTriggerPoints: SupportTriggerPointTriggerPoint[] = [
  'dashboard_checkout_studio_builder',
  'dashboard_checkout_studio_builder_greeting_message',
  'dashboard_checkout_studio_inspect',
];

const StartNewTriggerPoints: SupportTriggerPointTriggerPoint[] = [
  ...CheckoutStudioTriggerPoints,
  'assistant_onboarding',
];

const InjectOrNewTriggerPoints: SupportTriggerPointTriggerPoint[] = [
  'payments_analytics',
  'assistant_search_result_direct',
  'assistant_search_result_suggested_query',
  'terminal_shop_assistant_cta',
];

const RestartableTriggerPoints: SupportTriggerPointTriggerPoint[] = [
  'assistant_drawer_internal',
  'dashboard_help_menu_contact_support',
  'dashboard_help_icon',
  'dashboard_mobile_ios_app_get_help',
  'support_site_contact_support_link',
  'support_site_contact_url',
  'support_site_contact_us_link',
  'support_site_content',
  'support_site_i_cant_sign_in',
  'support_site_home_page_assistant',
  'link_mobile_get_support',
  'react_native_mobile_get_help',
];

export function isAssistantTriggerPoint(
  triggerPoint?: SupportTriggerPointTriggerPoint | string,
): boolean {
  return (
    !!triggerPoint &&
    AssistantTriggerPoints.includes(
      triggerPoint as SupportTriggerPointTriggerPoint,
    )
  );
}

export function isDotComCsfTriggerPoint(
  triggerPoint?: SupportTriggerPointTriggerPoint | string,
): boolean {
  return !!triggerPoint && triggerPoint === 'dot_com_embedded_csf';
}

export function isDotComCsfSelfServeRedirectTriggerPoint(
  triggerPoint?: SupportTriggerPointTriggerPoint | string,
): boolean {
  return !!triggerPoint && triggerPoint === 'dot_com_embedded_csf_self_serve';
}

export function isCheckoutStudioTriggerPoint(
  triggerPoint?: SupportTriggerPointTriggerPoint | string,
): boolean {
  return (
    !!triggerPoint &&
    CheckoutStudioTriggerPoints.includes(
      triggerPoint as SupportTriggerPointTriggerPoint,
    )
  );
}

export function isStartFreshTriggerPoint(
  triggerPoint?: SupportTriggerPointTriggerPoint | string,
): boolean {
  return (
    !!triggerPoint &&
    StartFreshTriggerPoints.includes(
      triggerPoint as SupportTriggerPointTriggerPoint,
    )
  );
}

export function getTriggerPointOpenBehavior(
  triggerPoint?: SupportTriggerPointTriggerPoint | string,
): TriggerPointOpenBehavior {
  if (
    !!triggerPoint &&
    InjectOrNewTriggerPoints.includes(
      triggerPoint as SupportTriggerPointTriggerPoint,
    )
  ) {
    return 'inject_or_new';
  }

  if (
    !!triggerPoint &&
    StartNewTriggerPoints.includes(
      triggerPoint as SupportTriggerPointTriggerPoint,
    )
  ) {
    return 'start_new';
  }

  return 'resume_latest';
}

export function isRestartableTriggerPoint(
  triggerPoint?: SupportTriggerPointTriggerPoint | string,
): boolean {
  return (
    !!triggerPoint &&
    RestartableTriggerPoints.includes(
      triggerPoint as SupportTriggerPointTriggerPoint,
    )
  );
}
